// Botão "Sincronizar com OMIE": o financeiro dispara quando quiser (sem agendador). A execução
// roda em segundo plano no backend (services/realizado_faturamento_omie.py); aqui acompanhamos o
// status a cada 4s e mostramos o resumo da última execução.
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { getRealizadoOmieStatus, sincronizarRealizadoOmie } from "../../api";
import { AvisoErro, Badge, Card, botaoPequeno, formatData, inputStyle } from "./comum";

const JANELAS = [30, 60, 90, 180, 365];

export default function SincronizacaoOmie({ C, onConcluido }) {
  const navigate = useNavigate();
  const [execucao, setExecucao] = useState(null);
  const [dias, setDias] = useState(90);
  const [erro, setErro] = useState("");
  const statusAnterior = useRef(null);

  const consultar = useCallback(() => getRealizadoOmieStatus().then(setExecucao).catch((e) => setErro(e.message)), []);

  useEffect(() => { consultar(); }, [consultar]);
  useEffect(() => {
    if (execucao?.status !== "EXECUTANDO") return undefined;
    const t = setInterval(consultar, 4000);
    return () => clearInterval(t);
  }, [execucao?.status, consultar]);
  useEffect(() => {
    if (statusAnterior.current === "EXECUTANDO" && execucao?.status === "OK") onConcluido?.();
    statusAnterior.current = execucao?.status;
  }, [execucao?.status, onConcluido]);

  const iniciar = async () => {
    setErro("");
    try { setExecucao(await sincronizarRealizadoOmie(dias)); } catch (e) { setErro(e.message); }
  };

  const executando = execucao?.status === "EXECUTANDO";
  const totais = Object.values(execucao?.resumo?.empresas || {}).reduce((acc, e) => {
    ["titulos", "nfsCriadas", "nfsAtualizadas", "casadas", "pagas", "semSolicitacao", "naoEncontradasNoOmie"].forEach((k) => { acc[k] = (acc[k] || 0) + (e[k] || 0); });
    return acc;
  }, {});
  const pendencias = Object.values(execucao?.resumo?.erros || {}).reduce((a, b) => a + b, 0);

  return (
    <Card
      C={C}
      titulo="Sincronizar com OMIE"
      subtitulo="Busca os títulos de Contas a Receber emitidos na janela escolhida e atualiza nº da nota, emissão, retenções, recebimento e status; vincula a nota à solicitação do formulário (CNPJ + valor + empresa). Não mexe na planilha — o script Nfe.py continua cuidando dela."
      acoes={
        <>
          <select value={dias} onChange={(e) => setDias(Number(e.target.value))} disabled={executando} style={{ ...inputStyle(C), minHeight: 34, padding: "5px 8px", fontSize: 12, width: "auto" }}>
            {JANELAS.map((d) => <option key={d} value={d}>Últimos {d} dias</option>)}
          </select>
          <button style={{ ...botaoPequeno(C.brand), opacity: executando ? 0.6 : 1 }} disabled={executando} onClick={iniciar}>
            <RefreshCw size={13} style={executando ? { animation: "spin 1s linear infinite" } : undefined} />
            {executando ? "Sincronizando…" : "Sincronizar agora"}
          </button>
        </>
      }
    >
      <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
      <AvisoErro C={C}>{erro}</AvisoErro>
      {!execucao ? (
        <div style={{ fontSize: 12, color: C.muted }}>Nenhuma sincronização feita ainda.</div>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center", fontSize: 12 }}>
          <Badge cor={execucao.status === "OK" ? C.success : execucao.status === "ERRO" ? C.danger : C.warning}>
            {{ OK: "Concluída", ERRO: "Erro", EXECUTANDO: "Em andamento" }[execucao.status] || execucao.status}
          </Badge>
          <span style={{ color: C.muted }}>
            {formatData(execucao.iniciadoEm)} {execucao.iniciadoEm.slice(11, 16)} · {execucao.usuario} · janela de {execucao.parametros?.dias} dias
          </span>
          {execucao.status === "OK" && (
            <>
              <span><b>{totais.titulos}</b> títulos</span>
              <span><b>{totais.nfsCriadas}</b> notas novas · <b>{totais.nfsAtualizadas}</b> atualizadas</span>
              <span><b>{totais.casadas}</b> vinculadas a solicitações</span>
              <span><b>{totais.pagas}</b> pagas</span>
              {pendencias > 0 && (
                <button onClick={() => navigate("/gestao-realizado/log-erros")} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", fontSize: 12, fontWeight: 700, padding: 0 }}>
                  {pendencias} pendência(s) no log →
                </button>
              )}
            </>
          )}
          {execucao.status === "ERRO" && <span style={{ color: C.danger }}>{(execucao.erro || "").split("\n")[0]}</span>}
        </div>
      )}
    </Card>
  );
}
